// The page's deployment settings, from config.json beside index.html (copied there by build.mjs; edit the deployed copy
// to change them without rebuilding).
import type { SpacetimeOptions } from './client/stdbLogin';
import type { SupabaseOptions } from './client/supabaseLogin';

/** One world the page can join: its own SpacetimeDB database, with its own characters. */
export interface WorldOption extends SpacetimeOptions {
  /** Stable key for the player's remembered choice (defaults to `<uri>/<database>`). */
  id: string;
  /** Shown in the world picker and while joining (defaults to the database name). */
  name: string;
  // `guests` (SpacetimeDB's own guest identities) is for local development worlds without sign-in; players' guests
  // are Supabase anonymous users (`supabase.guests`).
}

export interface PageConfig {
  /** Game data and authentication are separate services. Never empty. */
  worlds: WorldOption[];
  supabase: SupabaseOptions;
}

/** The defaults: the world on a local SpacetimeDB server (`spacetime start`), guests allowed. */
export const config: PageConfig = {
  worlds: [{ id: 'local', name: 'Ashenheart', uri: 'http://127.0.0.1:3000', database: 'ashenheart', guests: true }],
  supabase: { projectId: '', publishableKey: '', guests: false },
};

/** One `worlds` entry (or the older single `spacetime` block); null when it can't be joined. */
function readWorld(value: unknown, fallbackName: string): WorldOption | null {
  if (!value || typeof value !== 'object') return null;
  const w = value as Record<string, unknown>;
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const uri = text(w.uri), database = text(w.database);
  if (!uri || !database) return null;
  return { id: text(w.id) || `${uri}/${database}`, name: text(w.name) || fallbackName || database, uri, database,
    guests: w.guests === true };
}

/** Reads a parsed config.json over the defaults. `worlds` wins over the older single `spacetime` block. */
export function applyConfig(target: PageConfig, read: Record<string, unknown>): void {
  const worlds: WorldOption[] = [];
  if (Array.isArray(read.worlds)) {
    for (const entry of read.worlds) {
      const w = readWorld(entry, '');
      if (w && !worlds.some(o => o.id === w.id)) worlds.push(w);
      else console.warn('config.json: skipped a world entry', entry);
    }
  } else if (read.spacetime) {
    const w = readWorld(read.spacetime, 'Ashenheart');
    if (w) worlds.push(w);
  }
  if (worlds.length) target.worlds = worlds;
  const s = read.supabase as Record<string, unknown> | undefined;
  if (s && typeof s === 'object') {
    if (typeof s.projectId === 'string') target.supabase.projectId = s.projectId.trim();
    if (typeof s.publishableKey === 'string') target.supabase.publishableKey = s.publishableKey.trim();
    if (typeof s.guests === 'boolean') target.supabase.guests = s.guests;
  }
}

/** The world with this id, else the first one. */
export function pickWorld(worlds: WorldOption[], id: string): WorldOption {
  return worlds.find(w => w.id === id) ?? worlds[0];
}

/** Reads config.json; a missing or broken file leaves the defaults. */
export async function loadConfig(): Promise<void> {
  try {
    const response = await fetch('config.json', { cache: 'no-cache' });
    if (!response.ok) return;
    const read = await response.json();
    if (read && typeof read === 'object') applyConfig(config, read as Record<string, unknown>);
  } catch (e) {
    console.warn('config.json:', e);
  }
}
